import { useQuery } from '@tanstack/react-query'
import { Clock } from 'lucide-react'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { subscriptionPlansRoute, subscriptionTopUpRoute } from '@shared/settings/settings-deeplink'
import { useAuth } from '@renderer/chat/providers'
import { usePricingEdition } from '@renderer/chat/hooks/use-plan-features'
import { usePaywallState } from '@renderer/chat/store/paywalls'
import { useUnifiedBilling } from '@renderer/chat/store/global-billing'
import { v2TierFor } from '@renderer/chat/utils/billingUtils'
import { ranOutMidMeetingCopy, resetDateLabel } from '@renderer/components/pricing/limits-copy'
import { PlanUpgradeButton } from '@renderer/components/pricing/locked-page'
import { PlanNote } from '@renderer/components/pricing/plan-note'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { openSubscriptionSettings } from '../../../components/sidepanel/plan-banners/sidepanel-plan-banners'
import type { MeetingViewModel } from '../use-meeting-view-model'

/** GET /v1/audio-notes/:id/hours-ran-out: when the meeting ran past the hours it started with. */
function useHoursRanOutAt(audioNoteId: string | undefined, enabled: boolean): string | null {
  const { getAuthToken } = useAuth()
  const { data } = useQuery({
    queryKey: ['audio-note', audioNoteId, 'hours-ran-out'],
    enabled: enabled && Boolean(audioNoteId),
    staleTime: Infinity,
    queryFn: async (): Promise<string | null> => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute(`api/v1/audio-notes/${audioNoteId}/hours-ran-out`), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) {
        throw new Error(`Failed to load when the meeting's hours ran out (${response.status})`)
      }
      return ((await response.json()) as { data: { ranOutAt: string | null } }).data.ranOutAt
    },
  })
  return data ?? null
}

/**
 * On a finished meeting that outlasted the transcription hours: it was
 * transcribed to the end anyway, so the note says when they ran out and how to
 * get more. Free is sold Pro; a paid plan buys hours.
 */
export function HoursRanOutNote({ model }: { model: MeetingViewModel }) {
  const isRevamp = usePricingEdition() === 'v2-revamp'
  const paywall = usePaywallState()
  const { subscription } = useUnifiedBilling()
  const recording = model.variant === 'live' ? null : model.audioNote
  const ranOutAt = useHoursRanOutAt(recording?.id, isRevamp && Boolean(recording?.endedAt))

  if (!ranOutAt) {
    return null
  }
  const tier = v2TierFor(paywall.plan)
  const plan = tier ?? 'pro2'
  const time = new Date(ranOutAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  const copy = ranOutMidMeetingCopy(time, {
    plan: tier ?? 'free',
    upgradeTo: 'pro2',
    resets: resetDateLabel(subscription.currentPeriodEndAt),
  })

  const onAction = () => {
    AmplitudeRenderer.trackEvent('Paywall', {
      action: 'clicked',
      feature: 'hours-ran-out',
      reason: tier ? 'top-up' : 'upgrade',
      plan,
      from: 'meeting view',
    })
    openSubscriptionSettings(tier ? subscriptionTopUpRoute('hours') : subscriptionPlansRoute())
  }

  return (
    <PlanNote
      className="mx-3 mb-3"
      icon={<Clock />}
      plan={plan}
      title={copy.title}
      body={copy.body}
      actions={
        <PlanUpgradeButton plan={plan} size="sm" onClick={onAction}>
          {copy.cta}
        </PlanUpgradeButton>
      }
    />
  )
}
